
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import {
  courseMembersQuery,
  gamesQuery,
  classificationScoresQuery,
  normalScoresQuery,
  principlesScoresQuery,
  balancedEquationScoresQuery
} from '../api';

import { navigation } from '../data';
import { gameTypes } from './data';
import SubPagesLayout from '../../../UI/layout/subPages';

import './styles.css';
import Table from '../../../UI/table';

export default function InstructorScores() {
  const navigate = useNavigate();

  const [tableHeaders, setTableHeaders] = useState([]);
  const [tableData, setTableData] = useState([]);
  
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [csvData, setCsvData] = useState([]);

  const { data: courseMembers } = useQuery(courseMembersQuery()) || {};
  const { data: games } = useQuery(gamesQuery()) || {};
  const { data: classificationScores } = useQuery(classificationScoresQuery()) || {};
  const { data: normalScores } = useQuery(normalScoresQuery()) || {};
  const { data: principlesScores } = useQuery(principlesScoresQuery()) || {};
  const { data: balancedEquationScores } = useQuery(balancedEquationScoresQuery()) || {};

  const calculateGameTypeAverage = (student, game, scoresData) => {
    let scoresArray;
    
    switch (game.game_type) {
      case 'Account Classification':
        scoresArray = scoresData.classificationScores;
        break;
      case 'Normal Balance':
        scoresArray = scoresData.normalScores;
        break;
      case 'The Principles':
        scoresArray = scoresData.principlesScores;
        break;
      case 'Balanced Equation':
        scoresArray = scoresData.balancedEquationScores;
        break;
      default:
        return '--';
    }

    const matchingScores = scoresArray.filter(
      score => score.game === game.id && score.user === student.user_id
    );

    if (!matchingScores.length) return '--';
    
    const maxSession = Math.max(...matchingScores.map(s => s.game_session));
    const maxSessionScores = matchingScores.filter(
      score => score.game_session === maxSession
    );
    
    const average = maxSessionScores.length 
      ? maxSessionScores.reduce((sum, score) => sum + score['total_accuracy'], 0) / maxSessionScores.length 
      : '--';
    
    return typeof average === 'number' 
      ? `${(average * 100).toFixed(2)}%` 
      : '--';
  };

  useEffect(() => {
    if(courseMembers && games && classificationScores && normalScores && principlesScores && balancedEquationScores) {

      const studentsCourseMembers = courseMembers.filter(member => member.is_student);

      const activeGames = games.filter(game => game.is_active);

      let headers = [
        'Student\'s Name', 
        'Overall', 
        ...activeGames.map(game => `${game.name}<br><span class="thin">${gameTypes[game.game_type]}</span>`)
      ];
      setTableHeaders(headers);
      
      let scvHeaders = [
        'First Name', 
        'Last Name', 
        'Email', 
        'Overall', 
        ...activeGames.map(game => `${game.name}<br><span class="thin">${gameTypes[game.game_type]}</span>`)
      ];
      setCsvHeaders(scvHeaders);

      const tableDataArray = [];
      const csvDataArray = [];

      studentsCourseMembers.forEach(student => {
        const gameScores = activeGames.map(game => 
          calculateGameTypeAverage(
            student, 
            game, 
            {
              classificationScores,
              normalScores,
              principlesScores,
              balancedEquationScores
            }
          )
        );

        const numericScores = gameScores
          .filter(score => score !== '--')
          .map(score => parseFloat(score.replace('%', '')));

        const overallAccuracy = numericScores.length 
          ? (numericScores.reduce((sum, score) => sum + score, 0) / numericScores.length).toFixed(2) + '%'
          : '--';

        tableDataArray.push([
          `${student.first_name}${student.last_name ? ` ${student.last_name}` : ''}`,
          overallAccuracy,
          ...gameScores
        ]);

        csvDataArray.push([
          student.first_name || '',
          student.last_name || '',
          student.email || '',
          overallAccuracy,
          ...gameScores
        ]);
      });

      tableDataArray.sort((a, b) => a[0].toLowerCase().localeCompare(b[0].toLowerCase()));
      csvDataArray.sort((a, b) => {
        const aFullName = `${a[0]} ${a[1]}`.toLowerCase();
        const bFullName = `${b[0]} ${b[1]}`.toLowerCase();
        return aFullName.localeCompare(bFullName);
      });

      console.log('Table Data:', tableDataArray);
      console.log('CSV Data:', csvDataArray);

      setTableData(tableDataArray);
      setCsvData(csvDataArray);
    }
  }, [courseMembers, games, classificationScores, normalScores, principlesScores, balancedEquationScores]);

  const handlePrevButtonClick = () => {
    navigate('/instructor/insights/progress-scores/progress' );
  }

  console.log('tableHeaders', tableHeaders);

  return (
    <SubPagesLayout
      navigation={navigation}
      onFirstButtonClick={handlePrevButtonClick}
      currentPage={1}
      pagesQuantity={1}
      contentClassName="decision-insights-content-container"
    >
      <h1>Insights</h1>
      <h2>Scores | Accuracy Score</h2>
      <p>The scores below represent students' most recent completed attempt. For each assignment, they have up to three tries to improve their performance.</p>
      
      <Table 
        headers={tableHeaders}
        rows={tableData}
        csvHeaders={csvHeaders}
        csvRows={csvData}
      />
    </SubPagesLayout>
  );
}
